import { GpsAsLatLng } from "@/types/GpsAsLatLng";
import { MapItemSUP, MapItemWithCoodId } from "@/types/search";
import { useVirtualizer } from "@tanstack/react-virtual";
import React from "react";
import { SearchItem } from "../list/item";
const ITEM_HEIGHT = 400;
const SECTION_HEADER_HEIGHT = 48;
interface HeaderConfig {
  text: string;
  icon: React.ReactNode;
}
interface GenericProjectsListProps {
  projectData: MapItemWithCoodId[];
  columnsCount: number;
  headerContent?: ({
    count
  }: {
    count: number;
  }) => HeaderConfig | null;
}
const getSupItem = ({
  mapItem,
  sup
}: {
  mapItem: MapItemWithCoodId;
  sup: MapItemSUP;
}) => ({
  ...sup,
  mapCoordinates: mapItem._id,
  projectId: mapItem.projectId,
  projectName: mapItem.projectName,
  localityName: mapItem.localityName,
  propertyTypes: mapItem.propertyTypes,
  gps: sup.gps,
  updatedAt: sup.updatedAt ? new Date(sup.updatedAt) : undefined
});
const getProjectListData = ({
  projectData
}: {
  projectData: MapItemWithCoodId[];
}) => {
  if (!projectData || projectData.length === 0) return [];
  const seenIds = new Set<string>();
  const flattenedList = projectData.reduce((acc, item) => {
    const sups = item.sups || [];
    for (const sup of sups) {
      if (sup?._id && !seenIds.has(sup._id)) {
        seenIds.add(sup._id);
        acc.push(getSupItem({
          mapItem: item,
          sup
        }));
      }
    }
    return acc;
  }, [] as (MapItemSUP & {
    mapCoordinates: number[];
    gps?: GpsAsLatLng;
  })[]);
  return flattenedList;
};
export const GenericProjectsList = React.memo(function GenericProjectsList({
  projectData,
  columnsCount,
  headerContent
}: GenericProjectsListProps) {
  const parentRef = React.useRef<HTMLDivElement>(null);
  const listData = React.useMemo(() => getProjectListData({
    projectData
  }), [projectData]);
  const combinedListData = React.useMemo(() => {
    const combined: Array<(MapItemSUP & {
      mapCoordinates: number[];
      gps?: GpsAsLatLng;
    }) | {
      isSectionHeader: true;
      headerText: string;
      headerIcon: React.ReactNode;
      count: number;
    }> = [];
    if (listData.length > 0) {
      // Get header config if headerContent function is provided
      const headerConfig = headerContent?.({
        count: listData.length
      });
      if (headerConfig) {
        combined.push({
          isSectionHeader: true,
          headerText: headerConfig.text,
          headerIcon: headerConfig.icon,
          count: listData.length
        });
      }
      combined.push(...listData);
    }
    return combined;
  }, [listData, headerContent]);
  const gridRows = React.useMemo(() => {
    const rows: Array<{
      items: Array<(MapItemSUP & {
        mapCoordinates: number[];
        gps?: GpsAsLatLng;
      }) | {
        isSectionHeader: true;
        headerText: string;
        headerIcon: React.ReactNode;
        count: number;
      }>;
      isSectionHeader: boolean;
    }> = [];
    for (let i = 0; i < combinedListData.length; i++) {
      const item = combinedListData[i];
      if ("isSectionHeader" in item && item.isSectionHeader) {
        rows.push({
          items: [item],
          isSectionHeader: true
        });
        continue;
      }
      const rowItems: Array<MapItemSUP & {
        mapCoordinates: number[];
        gps?: GpsAsLatLng;
      }> = [item as MapItemSUP & {
        mapCoordinates: number[];
        gps?: GpsAsLatLng;
      }];
      for (let j = 1; j < columnsCount && i + j < combinedListData.length; j++) {
        const nextItem = combinedListData[i + j];
        if ("isSectionHeader" in nextItem && nextItem.isSectionHeader) {
          break;
        }
        rowItems.push(nextItem as MapItemSUP & {
          mapCoordinates: number[];
          gps?: GpsAsLatLng;
        });
      }
      rows.push({
        items: rowItems,
        isSectionHeader: false
      });
      i += rowItems.length - 1;
    }
    return rows;
  }, [combinedListData, columnsCount]);
  const virtualizer = useVirtualizer({
    count: gridRows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: index => {
      return gridRows[index]?.isSectionHeader ? SECTION_HEADER_HEIGHT : ITEM_HEIGHT;
    },
    overscan: 2
  });
  if (listData.length === 0) return null;
  return <div ref={parentRef} className="w-full" style={{
    overflowY: "auto"
  }}>
			<div style={{
      height: `${virtualizer.getTotalSize()}px`,
      width: "100%",
      position: "relative"
    }}>
				{virtualizer.getVirtualItems().map(virtualRow => {
        const row = gridRows[virtualRow.index];
        return <div key={virtualRow.key} style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: `${virtualRow.size}px`,
          transform: `translateY(${virtualRow.start}px)`
        }}>
							{row.isSectionHeader ? <div className="sticky top-0 z-10 px-4 py-3 bg-white border-b border-gray-200 shadow-sm">
									{"isSectionHeader" in row.items[0] && <div className="flex items-center gap-2">
											{row.items[0].headerIcon}
											<h2 className="text-base font-semibold text-gray-900">{row.items[0].headerText}</h2>
										</div>}
								</div> : <div className="grid gap-4 h-full p-4" style={{
            gridTemplateColumns: `repeat(${columnsCount}, minmax(0, 1fr))`
          }}>
									{row.items.filter((item_0): item_0 is MapItemSUP & {
              mapCoordinates: number[];
              gps?: GpsAsLatLng;
            } => !("isSectionHeader" in item_0)).map((item_1, columnIndex) => <SearchItem data={item_1} rank={virtualRow.index * columnsCount + columnIndex + 1} key={item_1._id || `${virtualRow.index}-${columnIndex}`} className="h-[370px]" />)}
								</div>}
						</div>;
      })}
			</div>
		</div>;
});